import { useEffect } from "react";
import { useReactTable, getCoreRowModel, ColumnDef } from "@tanstack/react-table";
import { useTableStateStore } from "@/hooks/useTableStateStore";
import { InitialValues } from "@/utils/TableStateStore";
import { reaction } from "mobx";
import { QueryParams } from "@/utils/mockServerTableQuery";
import { useUrlQuerySync } from "./useUrlQuerySync";
interface UseServerTableOptions<TData> {
	columns: ColumnDef<TData, any>[];
	data: TData[];
	fetchFn: (params: QueryParams) => void;
	initialValues?: InitialValues;
	enableUrlParams?: boolean;
	initFromUrl?: boolean;
	allData?: TData[];
}

const getSortingQueryParams = (sorting: { id: string; desc: boolean }[]): Dryp.SortQuery | undefined => {
	if (sorting?.length > 0) {
		return {
			sort: sorting?.[0]?.id,
			direction: sorting?.[0]?.desc ? "desc" : "asc",
		};
	}
	return undefined;
};

export const useServerTable = <TData>({
	columns,
	data,
	fetchFn,
	initialValues,
	enableUrlParams = false,
	initFromUrl = false,
	allData,
}: UseServerTableOptions<TData>) => {
	const tableState = useTableStateStore(initialValues || null);

	useUrlQuerySync(
		{
			page: tableState.page,
			pageSize: tableState.pageSize,
			filters: tableState.filtersSnapshot,
			sort: tableState.sortingSnaphot,
		},
		{
			setPage: tableState.setPage,
			setPageSize: tableState.setPageSize,
			setFilters: tableState.setFilters,
			setSorting: tableState.setSorting,
		},
		{
			enableUrlParams,
			initFromUrl,
		},
	);

	useEffect(() => {
		const dispose = reaction(
			() => ({
				page: tableState.page,
				pageSize: tableState.pageSize,
				sorting: [...tableState.sortingSnaphot],
				filters: tableState.filtersSnapshot,
			}),
			() => {
				fetchFn({
					page: tableState.page,
					pageSize: tableState.pageSize,
					sort: getSortingQueryParams(tableState.sortingSnaphot),
					filters: tableState.filtersSnapshot,
				});
			},
			{ fireImmediately: true },
		);
		return () => dispose();
	}, []);

	useEffect(() => {}, [tableState.columnVisibility]);

	const refresh = () => {
		fetchFn({
			page: tableState.page,
			pageSize: tableState.pageSize,
			sort: getSortingQueryParams(tableState.sortingSnaphot),
			filters: tableState.filtersSnapshot,
		});
	};
	const table = useReactTable({
		data,
		columns,
		manualPagination: true,
		manualSorting: true,
		manualFiltering: true,
		enableRowSelection: true,
		pageCount: Math.ceil(data.length / tableState.pageSize),
		getCoreRowModel: getCoreRowModel(),
		getRowId: (row, index) => {
			const r = row as any;
			if (r && (typeof r.id === "string" || typeof r.id === "number")) {
				return String(r.id);
			}
			return `__row_${index}`;
		},

		state: {
			sorting: tableState.sortingSnaphot,
			columnFilters: tableState.columnFilters,
			columnVisibility: tableState.columnVisibilitySnaphot,
			rowSelection: tableState.rowSelectionSnapshot,
			pagination: {
				pageIndex: tableState.page,
				pageSize: tableState.pageSize,
			},
		},

		onSortingChange: updater => {
			tableState.setSorting(updater);
		},
		onColumnFiltersChange: updater => {
			tableState.setFilters(updater);
			tableState.setPage(0);
		},
		onColumnVisibilityChange: tableState.setColumnVisibility,
		onRowSelectionChange: updater => {
			tableState.setRowSelection(updater);
		},
		meta: { allData, setPage: tableState.setPage, setPageSize: tableState.setPageSize, refresh, tableState },
	});

	return { table, tableState, refresh };
};
